/**
 * 원(지점)별 TV 전광판 페이지 — 2026-06-21 (2026-06-22 밝은 테마 + 16:9 풀스크린 레이아웃)
 * 공개(인증 불필요). /display/:token 으로 열며, 토큰으로 원을 해석(격리).
 * 공지 + 랭킹(우리 지점/전체 × 이번 주/이번 달). 60초 자동 새로고침.
 * 한 화면(16:9)에 스크롤 없이 들어가도록 height:100vh + overflow:hidden 고정.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';

interface Rank {
  rank: number;
  name: string;
  study_hours: string;
  total_minutes: number;
  org_name?: string;
}
interface Notice {
  title: string;
  content: string;
  is_pinned: boolean;
}
interface Board {
  organization: { id: string; name: string };
  notices: Notice[];
  ranking_org_weekly: Rank[];
  ranking_org_monthly: Rank[];
  ranking_global_weekly: Rank[];
  ranking_global_monthly: Rank[];
  /** 공지 글씨 배율 — 매장이 설정에서 고른다. 없으면 1(지금 그대로). 2026-09-06 */
  notice_font_scale?: number;
  as_of: string;
}

const MEDALS = ['🥇', '🥈', '🥉'];
const RANK_BG = ['#fde68a', '#e5e7eb', '#fed7aa']; // 금/은/동 배지 배경

/** 수능일 = 11월 셋째 목요일 (근사). 매년 자동 롤오버. */
function thirdThursdayOfNov(year: number): Date {
  const first = new Date(year, 10, 1); // 11월 1일
  const offset = (4 - first.getDay() + 7) % 7; // getDay: 0=일 … 4=목
  return new Date(year, 10, 1 + offset + 14); // 첫 목요일 + 2주
}
function suneungDDay(now: Date): { label: string; dateLabel: string } {
  const todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let exam = thirdThursdayOfNov(now.getFullYear());
  if (todayMid.getTime() > exam.getTime()) exam = thirdThursdayOfNov(now.getFullYear() + 1); // 수능 다음날부터 내년으로
  const diff = Math.round((exam.getTime() - todayMid.getTime()) / 86400000);
  return {
    label: diff === 0 ? 'D-DAY' : `D-${diff}`,
    dateLabel: `${exam.getFullYear()}.${exam.getMonth() + 1}.${exam.getDate()}`,
  };
}

/** 2×2 그리드 한 칸 — 헤더 + 스크롤 가능한 순위 목록(카드 높이 안에서만 스크롤) */
const RankCard: React.FC<{ title: string; rows: Rank[]; accent: string; bg: string; showOrg?: boolean }> = ({ title, rows, accent, bg, showOrg }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      // 🔴 minWidth:0 이 없으면 격자 칸이 안쪽 글자 길이만큼 벌어져 이름 줄임(…)이 안 먹는다
      minWidth: 0,
      background: '#ffffff',
      borderRadius: 18,
      padding: '1vh 0.8vw',
      boxShadow: '0 6px 18px rgba(15, 23, 42, 0.06)',
      border: '1px solid #eef2f7',
    }}
  >
    <h3 style={{ fontSize: 'clamp(14px, 1.8vh, 24px)', fontWeight: 900, margin: 0, marginBottom: '0.6vh', color: accent, flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</h3>
    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: '0.5vh' }}>
      {rows.length === 0 ? (
        <div style={{ color: '#9ca3af', fontSize: 'clamp(15px, 1.8vh, 23px)' }}>아직 기록이 없습니다</div>
      ) : (
        rows.slice(0, 5).map((r) => {
          const top = r.rank <= 3;
          return (
            <div
              key={r.rank}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5vw',
                padding: '0.5vh 0.5vw',
                borderRadius: 10,
                background: top ? bg : '#f8fafc',
                flexShrink: 0,
                minWidth: 0,
              }}
            >
              <span
                style={{
                  width: 'clamp(28px, 3.8vh, 44px)',
                  height: 'clamp(28px, 3.8vh, 44px)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(14px, 1.9vh, 24px)',
                  fontWeight: 900,
                  background: top ? RANK_BG[r.rank - 1] : '#e2e8f0',
                  color: '#334155',
                  flexShrink: 0,
                }}
              >
                {top ? MEDALS[r.rank - 1] : r.rank}
              </span>
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <span style={{ fontSize: 'clamp(15px, 2.0vh, 26px)', fontWeight: 700, color: '#1e293b' }}>{r.name}</span>
                {showOrg && r.org_name && (
                  <span style={{ fontSize: 'clamp(11px, 1.4vh, 17px)', fontWeight: 600, color: '#64748b', marginLeft: '0.3vw' }}>({r.org_name})</span>
                )}
              </span>
              <span style={{ fontSize: 'clamp(13px, 1.8vh, 23px)', fontWeight: 800, color: accent, whiteSpace: 'nowrap', flexShrink: 0 }}>{r.study_hours}</span>
            </div>
          );
        })
      )}
    </div>
  </div>
);

export default function DisplayBoardPage() {
  const { token } = useParams<{ token: string }>();
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState('');
  const [now, setNow] = useState<Date>(() => new Date());

  // 시계: 1초마다 갱신 (데이터 폴링과 별개로 계속 움직임)
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/v1/public/display/board?token=${encodeURIComponent(token || '')}`);
      if (!res.ok) {
        setError('유효하지 않은 디스플레이 주소입니다');
        return;
      }
      setError('');
      setBoard(await res.json());
    } catch {
      setError('연결 오류 — 잠시 후 다시 시도합니다');
    }
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 300000); // 5분마다 데이터 새로고침
    return () => clearInterval(t);
  }, [load]);

  const pageBg = 'linear-gradient(135deg, #eef4ff 0%, #f7fbff 45%, #fff7f0 100%)';
  const center: React.CSSProperties = { height: '100vh', width: '100vw', background: pageBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700 };

  if (error && !board) return <div style={{ ...center, color: '#ef4444' }}>{error}</div>;
  if (!board) return <div style={{ ...center, color: '#64748b' }}>불러오는 중...</div>;

  // 공지 글씨 배율 (2026-09-06 대표 지시) — 매장이 설정에서 고른다.
  // 🔴 공지에만 쓴다. 순위 칸에 쓰면 이름이 잘리고 칸이 서로 밀린다.
  // 값이 없거나 이상하면 1 = 지금 보이는 그대로.
  const rawScale = Number(board.notice_font_scale);
  const ns = Number.isFinite(rawScale) && rawScale >= 0.7 && rawScale <= 2 ? rawScale : 1;
  const nfz = (min: number, vh: number, max: number) =>
    `clamp(${Math.round(min * ns)}px, ${(vh * ns).toFixed(2)}vh, ${Math.round(max * ns)}px)`;

  return (
    <div
      style={{
        height: '100vh',
        width: '100vw',
        background: pageBg,
        color: '#0f172a',
        padding: '2.2vh 1.8vw',
        boxSizing: 'border-box',
        fontFamily: 'Pretendard, system-ui, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* 헤더 */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, marginBottom: '1.8vh' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.9vw' }}>
          <span style={{ display: 'inline-block', width: 'clamp(6px, 0.6vw, 12px)', height: 'clamp(32px, 5.2vh, 64px)', borderRadius: 6, background: 'linear-gradient(180deg, #6366f1, #06b6d4)' }} />
          <h1 style={{ fontSize: 'clamp(30px, 5.2vh, 64px)', fontWeight: 900, margin: 0, letterSpacing: '-0.02em' }}>{board.organization.name}</h1>
        </div>

        {/* 수능 D-day */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7vw', background: 'linear-gradient(135deg, #ef4444, #f59e0b)', color: '#fff', padding: '0.7vh 1.5vw', borderRadius: 999, boxShadow: '0 6px 20px rgba(239, 68, 68, 0.28)' }}>
          <span style={{ fontSize: 'clamp(16px, 2.4vh, 30px)', fontWeight: 800 }}>📚 수능</span>
          <span style={{ fontSize: 'clamp(25px, 4.1vh, 53px)', fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1 }}>{suneungDDay(now).label}</span>
          <span style={{ fontSize: 'clamp(12px, 1.6vh, 18px)', fontWeight: 600, opacity: 0.9 }}>{suneungDDay(now).dateLabel}</span>
        </div>

        <span style={{ fontSize: 'clamp(14px, 2.0vh, 23px)', color: '#475569', background: '#ffffff', padding: '0.8vh 1.1vw', borderRadius: 999, boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)', fontWeight: 600, whiteSpace: 'nowrap' }}>
          🕐 {now.toLocaleString('ko-KR', { dateStyle: 'long', timeStyle: 'medium' })}
        </span>
      </div>

      {/* 본문: 왼쪽 절반 공지 / 오른쪽 절반 랭킹 2×2 (2026-09-06 대표 지시)
          종전엔 공지 32% · 랭킹 68% 이라 공지 글이 좁은 칸에서 줄바꿈으로 뭉개졌다.
          "순위 있는 것은 오른쪽으로 모으고 공지사항을 왼쪽 절반으로. 순위는 조금 작아져도 된다." */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: '1.6vw' }}>
        {/* 공지 (왼쪽 절반) */}
        <div
          style={{
            width: '50%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            background: '#ffffff',
            borderRadius: 20,
            padding: '1.6vh 1.2vw',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.07)',
            borderLeft: '10px solid #f59e0b',
          }}
        >
          <h2 style={{ fontSize: nfz(23, 3.2, 39), fontWeight: 900, margin: 0, marginBottom: '1.2vh', color: '#d97706', flexShrink: 0 }}>📢 공지사항</h2>
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {board.notices.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: nfz(17, 2.3, 28) }}>등록된 공지가 없습니다</div>
            ) : (
              board.notices.map((n, i) => (
                <div key={i} style={{ padding: '1.2vh 0', borderBottom: i < board.notices.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <div style={{ fontSize: nfz(21, 3.0, 37), fontWeight: 800, color: '#1e293b' }}>
                    {n.is_pinned && <span style={{ marginRight: 8 }}>📌</span>}
                    {n.title}
                  </div>
                  {n.content && <div style={{ fontSize: nfz(16, 2.3, 28), color: '#475569', marginTop: 6, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{n.content}</div>}
                </div>
              ))
            )}
          </div>
        </div>

        {/* 랭킹 2×2 (오른쪽 절반) — 남은 폭을 그대로 채운다 */}
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', gap: '1vh 1vw' }}>
          <RankCard title="🏆 우리 지점 · 이번 주" rows={board.ranking_org_weekly} accent="#0d9488" bg="#ecfdf5" />
          <RankCard title="🏆 우리 지점 · 이번 달" rows={board.ranking_org_monthly} accent="#059669" bg="#ecfdf5" />
          <RankCard title="🌍 전체 · 이번 주" rows={board.ranking_global_weekly} accent="#2563eb" bg="#eff6ff" showOrg />
          <RankCard title="🌍 전체 · 이번 달" rows={board.ranking_global_monthly} accent="#4f46e5" bg="#eef2ff" showOrg />
        </div>
      </div>
    </div>
  );
}
